import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Avatar, Box, Button, Chip, Divider, FormControl, IconButton, MenuItem, Modal, Select, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import CalendarIcon from "../../../components/icons/ui-actions/CalendarIcon";
import { getScoreColor, iconMapping } from "../../../helpers/leadsManagement";
import EditIcon from "../../../icons/EditIcon";
import EmailIcon from "../../../icons/EmailIcon";
import FemaleAvatar from "../../../icons/FemaleAvatar";
import HumanIcon from "../../../icons/HumanIcon";
import PhoneIcon from "../../../icons/PhoneIcon";
import { useUserStore } from "../../../stores/userStore";
import { ActivityData, LeadData, UpdateLeadDetailPayload } from "../../../types/leads.types";
import { formatDateToMMDDYYYY, formatDateWithTimeValue } from "../../../utils/date";

interface Props {
  open: boolean;
  onClose: () => void;
  isDetailUpdating: boolean;
  leadData: LeadData | null;
  editStatus: { code: string; shortDescription: string; longDescription: string } | null;
  editNotes: string;
  onEditStatus: (val: { code: string; shortDescription: string; longDescription: string }) => void;
  onEditNotes: (val: string) => void;
  customerStatusOptions: Array<{ code: string; shortDescription: string; longDescription: string }>;
  onUpdateLeadDetailSubmit: (data: UpdateLeadDetailPayload) => void;
  canEdit: boolean;
  activityData: ActivityData;
  originalRemarks?: string;
  originalCustomerResponse?: string;
}

const LeadDetailModal = ({ open, onClose, isDetailUpdating, leadData, onEditStatus, onEditNotes, editStatus, editNotes, customerStatusOptions, onUpdateLeadDetailSubmit, canEdit, activityData, originalRemarks = "", originalCustomerResponse = "" }: Props) => {
  const { t } = useTranslation();
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState(editNotes);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);
  const { user } = useUserStore()
  const hasChange = editNotes?.trim() || editStatus
  const updateDisabled = !hasChange || isDetailUpdating;

  if (!leadData) return null;

  // Helper function to get user initials
  const getUserInitials = (userName: string) => {
    return userName
      .split(" ")
      .map(name => name.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  // Helper function to get avatar color based on user name
  const getAvatarColor = (userName: string) => {
    const colors = ["#E87722", "#4caf50", "#9c27b0", "#2196f3", "#f44336", "#ff9800", "#795548"];
    const index = userName.length % colors.length;
    return colors[index];
  };

  // Helper function to get action chip color
  const getActionChipStyle = (action: string) => {
    switch (action.toLowerCase()) {
      case "create":
        return { backgroundColor: "#e8f5e8", color: "#2e7d32" };
      case "update":
        return { backgroundColor: "#e3f2fd", color: "#1976d2" };
      case "delete":
        return { backgroundColor: "#ffebee", color: "#d32f2f" };
      default:
        return { backgroundColor: "#f5f5f5", color: "#666" };
    }
  };

  const findCustomerStatusOptionDetail = (responseCode: string) => {
    const details = customerStatusOptions.find((item) => item.code === responseCode) || null
    if (!details) return null
    return {
      longDesc: details.longDescription,
      shortDesc: details.shortDescription
    }
  }



  const onSubmit = () => {
    setIsHistoryExpanded(true);
    onUpdateLeadDetailSubmit({
      id: leadData.id,
      remarks: editNotes && editNotes !== originalRemarks ? editNotes : "",
      customerResponse: editStatus?.code && editStatus?.code !== originalCustomerResponse ? editStatus?.code : "",
      user: user?.name || "",
    });
  }

  const showSuggestEtaText = leadData?.status?.toLowerCase() === "new" && !!leadData?.assignedChannel && !!leadData?.suggestEtaText

  return (
    <Modal
      open={open}
      onClose={() => { onClose(); setIsHistoryExpanded(true) }}
      aria-labelledby="lead-detail-modal"
      aria-describedby="lead-detail-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "600px" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            p: 3,
            borderBottom: "1px solid #e0e0e0",
            background: "#FEF9F4"
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            <FemaleAvatar />
            <Box>
              <Typography fontWeight="600" sx={{ mb: 0.5 }}>
                {leadData.fullName || ""}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                {
                  !!leadData.emailAddress && <>
                    <EmailIcon />
                    <Typography color="#E87722" sx={{ fontSize: "0.9rem" }}>
                      {leadData.emailAddress}
                    </Typography>
                  </>
                }
                {
                  !!leadData.contactPhoneNumber && <>
                    <PhoneIcon />
                    <Typography sx={{ fontSize: "0.9rem" }}>
                      {leadData.contactPhoneNumber}
                    </Typography>
                  </>
                }
                <Box sx={{ ml: 3, display: "flex", alignItems: "center", gap: 0.5 }}>
                  <HumanIcon />
                  <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
                    {leadData.gender === "*" ? t("platform.notToDisclose") : leadData.gender}
                  </Typography>
                </Box>
              </Box>

              <Typography color="text.secondary" sx={{ fontSize: "0.9rem", mt: 0.5 }}>
                {leadData.channel || ""}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, maxHeight: "70vh", overflow: "auto" }}>
          {/* First Row: Qualification and Lead Score */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 4,
              mb: 3,
            }}
          >
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.qualification")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: "1.2rem" }}>{iconMapping(leadData.qualification?.toLocaleLowerCase(), { width: 20, height: 20 })}</Typography>
                <Typography fontWeight="500">{leadData.qualification}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.leadScore")}
              </Typography>
              <Avatar
                sx={{
                  bgcolor: getScoreColor(leadData.leadScore),
                  width: 32,
                  height: 32,
                  fontSize: "1rem",
                  fontWeight: "bold",
                }}
              >
                {leadData.leadScore || "9"}
              </Avatar>
            </Box>
          </Box>

          {/* Second Row: Lead type, Status, Assigned channel */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 3,
              mb: 3,
            }}
          >
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.leadType")}
              </Typography>
              <Chip
                label={leadData.leadType || ""}
                variant="outlined"
                size="small"
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "white",
                  borderColor: "#d0d0d0",
                }}
              />
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.status")}
              </Typography>
              <Chip
                label={leadData.status || ""}
                size="small"
                sx={{
                  backgroundColor: "#E87722",
                  color: "white",
                  borderRadius: "8px",
                  fontWeight: "500",
                }}
              />
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.assignedChannel")}
              </Typography>
              <Typography fontWeight="500">
                {leadData.assignedChannel || ""}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Third Row: Suggested type, Products of interest, Communication channel */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 3,
              mb: 3,
            }}
          >
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.suggestedType")}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {
                  leadData.suggestedType.map((item, index) => {
                    return (
                      <Chip
                        key={index}
                        label={item}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "white",
                          borderColor: "#d0d0d0",
                          fontSize: "0.75rem",
                        }}
                      />
                    )
                  })
                }
              </Box>
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.productsOfInterest")}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {
                  leadData.preferredProductTypes.map((item, index) => {
                    return (
                      <Chip
                        key={index}
                        label={item}
                        variant="outlined"
                        size="small"
                        sx={{
                          borderRadius: "16px",
                          backgroundColor: "white",
                          borderColor: "#d0d0d0",
                          fontSize: "0.75rem",
                        }}
                      />
                    )
                  })
                }

              </Box>
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.communicationChannel")}
              </Typography>
              <Typography fontWeight="500">
                {leadData.channel || ""}
              </Typography>
            </Box>
          </Box>

          {/* Fourth Row: Created on, Last contacted on, Suggested ETA */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 3,
              mb: 4,
            }}
          >
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.createdOn")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarIcon />
                <Typography>{formatDateToMMDDYYYY(leadData?.createdOn) || ""}</Typography>
              </Box>
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.lastContactedOn")}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarIcon />
                <Typography>
                  {formatDateToMMDDYYYY(leadData.lastContactDate) || ""}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem" }}>
                {t("platform.suggestedETA")}
              </Typography>
              {
                showSuggestEtaText && <Chip
                  label={leadData.suggestEtaText}
                  size="small"
                  sx={{
                    backgroundColor: "#E87722",
                    color: "white",
                    borderRadius: "8px",
                    fontWeight: "500",
                  }}
                />
              }
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Notes */}
          <Box sx={{ mb: 3 }}>
            <Typography color="text.secondary" gutterBottom sx={{ fontSize: "0.9rem", marginBottom: "16px" }}>
              {t("platform.notes")}
            </Typography>
            {!isEditingNotes ? (
              <Box
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                  minHeight: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontSize: "0.9rem", flex: 1 }} color="#00000099">
                  {editNotes ? editNotes : t("platform.addNotes")}
                </Typography>
                {
                  canEdit && <IconButton
                    onClick={() => {
                      setIsEditingNotes(true);
                      setTempNotes(editNotes);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                }

              </Box>
            ) : (
              <Box
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                  overflow: "hidden", // Ensures child elements respect the border radius
                }}
              >
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={tempNotes}
                  onChange={(e) => setTempNotes(e.target.value)}
                  placeholder={t("platform.addYourNotesHere")}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent",
                      borderRadius: "12px 12px 0 0", // Only round top corners since bottom has buttons
                      "& fieldset": {
                        border: "none",
                      },
                      "&:hover fieldset": {
                        border: "none",
                      },
                      "&.Mui-focused fieldset": {
                        border: "none",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "16px",
                      fontSize: "0.9rem",
                      lineHeight: "1.5",
                    },
                  }}
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                    p: 2,
                    borderTop: "1px solid #e0e0e0",
                    borderRadius: "0 0 12px 12px", // Round bottom corners
                    backgroundColor: "#f9f9f9",
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => {
                      setIsEditingNotes(false);
                      setTempNotes(editNotes);
                    }}
                    sx={{
                      color: "text.secondary",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                  >
                    {t("platform.cancel")}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      onEditNotes(tempNotes);
                      setIsEditingNotes(false);
                    }}
                    sx={{
                      backgroundColor: "#E87722",
                      color: "white",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#e67c00",
                      },
                    }}
                  >
                    {t("platform.ok")}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>

          <Divider sx={{ borderColor: "BDBFE1", my: "24px", }} />

          {/* New status */}
          <Box sx={{ mb: 3 }}>
            <Typography color="text.secondary" sx={{ fontSize: "0.9rem", marginBottom: "16px" }}>
              {t("platform.newStatus")}
            </Typography>

            <FormControl fullWidth>
              <Select
                value={editStatus?.code || ""}
                disabled={!canEdit}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  const selectedOption = customerStatusOptions.find(opt => opt.code === selectedValue);
                  if (selectedOption) {
                    onEditStatus(selectedOption);
                  }
                }}
                displayEmpty
                IconComponent={ExpandMoreIcon}
                sx={{
                  backgroundColor: "#f9f9f9",
                  borderRadius: "12px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e0e0e0",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#d0d0d0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ff8c00",
                  },
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    padding: "12px 16px",
                  },
                  "& .MuiSelect-icon": {
                    top: "50%",
                    transform: "translateY(-50%)",
                    right: "12px",
                    color: "text.secondary",
                  },
                }}
                renderValue={(selected) => {
                  if (!selected || !editStatus) {
                    return (
                      <Typography color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                        {t("platform.selectASuggestion")}
                      </Typography>
                    );
                  }
                  return (
                    <Typography sx={{ fontSize: "0.9rem", fontWeight: "500" }}>
                      {editStatus.longDescription}
                    </Typography>
                  );
                }}
              >
                {customerStatusOptions.map((option) => (
                  <MenuItem key={option.code} value={option.code} disabled={!!(leadData?.customerResponse) && leadData?.customerResponse === option.code}>
                    <Box sx={{ py: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: "500",
                          fontSize: "0.9rem",
                          mb: 0.5
                        }}
                      >
                        {option.longDescription}
                      </Typography>
                      <Typography color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                        {option.shortDescription}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Description field that appears below when option is selected */}
            {editStatus && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <Typography color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                  {editStatus.shortDescription}
                </Typography>
              </Box>
            )}
          </Box>

          {/* View status history */}
          <Box sx={{ mb: 4 }}>
            <Button
              variant="text"
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: isHistoryExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s ease"
                  }}
                />
              }
              onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
              sx={{
                color: "text.secondary",
                textTransform: "none",
                fontSize: "0.9rem",
                width: "100%",
                justifyContent: "center",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "text.primary",
                },
              }}
            >
              {t("platform.viewStatusHistory")}
            </Button>

            {/* Activity Log - Expandable */}
            {isHistoryExpanded && (
              <Box
                sx={{
                  mt: 3,
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  backgroundColor: "#f9f9f9",
                  overflow: "hidden"
                }}
              >
                <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "white" }}>
                  <Typography fontWeight="600" sx={{ fontSize: "0.95rem" }}>
                    {t("platform.activityLog")}
                  </Typography>
                </Box>

                {/* Activity Items */}
                <Box sx={{ p: 0 }}>
                  {activityData?.records && activityData?.records?.length > 0 ? (
                    activityData.records.map((activity, index) => {
                      const responseData = findCustomerStatusOptionDetail(activity.details.customerResponse)

                      return <Box
                        key={activity.id}
                        sx={{
                          p: 3,
                          borderBottom: index < activityData.records.length - 1 ? "1px solid #e0e0e0" : "none",
                          backgroundColor: "white"
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: getAvatarColor(activity.user),
                              fontSize: "0.8rem",
                              fontWeight: "600"
                            }}
                          >
                            <FemaleAvatar />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                              <Typography fontWeight="600" sx={{ fontSize: "0.9rem" }}>
                                {activity.user}
                              </Typography>
                              <Chip
                                label={activity.action}
                                size="small"
                                sx={{
                                  ...getActionChipStyle(activity.action),
                                  fontSize: "0.75rem",
                                  height: "20px"
                                }}
                              />
                            </Box>
                            <Typography color="text.secondary" sx={{ fontSize: "0.8rem", mb: 1 }}>
                              {formatDateWithTimeValue(activity.createdDateTime)}
                            </Typography>
                            {activity.details && (
                              <Box sx={{ mt: 1 }}>
                                {activity.details.customerResponse && (
                                  <Typography sx={{ fontSize: "0.85rem", mb: 1 }}>
                                    <strong>{t("platform.customerResponse")}</strong> {`${responseData?.longDesc} (${responseData?.shortDesc})`}
                                  </Typography>
                                )}
                                {activity.details.remarks && (
                                  <Typography sx={{ fontSize: "0.85rem" }}>
                                    <strong>{t("platform.remarks")}</strong> {activity.details.remarks}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    })
                  ) : (
                    <Box sx={{ p: 3, textAlign: "center", backgroundColor: "white" }}>
                      <Typography color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                        {t("platform.noActivityHistory")}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>

          {/* Update Button */}
          {
            canEdit && <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                fullWidth
                disabled={updateDisabled}
                sx={{
                  backgroundColor: hasChange ? "#E87722" : "#e0e0e0",
                  color: hasChange ? "white" : "#999",
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: "600",
                  py: 1.5,
                  fontSize: "1rem",
                  cursor: hasChange ? "pointer" : "not-allowed",
                  "&:hover": {
                    backgroundColor: hasChange ? "#e67c00" : "#e0e0e0",
                  },
                  "&:disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#999",
                    cursor: "not-allowed",
                  },
                }}
                onClick={onSubmit}
              >
                {t("platform.update")}
              </Button>
            </Box>
          }

        </Box>
      </Box>
    </Modal>
  );
};

export default LeadDetailModal;